<template>
  <SimpleReviewSummary v-if="policySummary" :title="policySummary.title" :icon="policySummary.icon">
    <template #reviewSummaryItems>
      <ReviewSummaryItem
        v-for="(item, itemIndex) in policySummary?.items"
        :key="itemIndex"
        :item="item"
        :isLastReviewSummaryItem="!((itemIndex as number) < policySummary.items.length - 1)"
      >
        <template #title>
          <div class="relative">
            <div class="text-racq-dark-grey text-base-bold" v-html="item.fields.title.value"></div>
          </div>
        </template>
        <template #content>
          <div class="text-base" v-html="fillBookmarks(item.fields.content)"></div>
        </template>
        <template #secondaryLinks>
          <div
            v-if="item.fields?.firstLink.value.class || item.fields?.secondLink.value.class"
            class="flex flex-col md:flex-row gap-6 md:gap-4 mt-2 self-stretch"
          >
            <div v-if="item.fields?.firstLink.value.class === 'CertificateOfInsurance'">
              <DocumentLink
                :enabled="currentPolicy.currentDocumentId?.length > 0"
                :linkText="item.fields?.firstLink.value.text"
                :enabledIcon="require('@/assets/icons/document-icon-blue.svg')"
                :disabledIcon="require('@/assets/icons/document-icon-grey.svg')"
                dataTestId="btnCertificateOfInsurance"
                @linkClicked="() => downloadDocument(currentPolicy?.currentDocumentId)"
              ></DocumentLink>
            </div>
            <div
              v-if="
                item.fields?.secondLink.value.class === 'CertificateOfCurrency' &&
                currentPolicy.isCertOfCurrencyGenAllowed
              "
            >
              <DocumentLink
                :enabled="true"
                :linkText="item.fields?.secondLink.value.text"
                :enabledIcon="require('@/assets/icons/document-icon-blue.svg')"
                :disabledIcon="require('@/assets/icons/document-icon-grey.svg')"
                dataTestId="btnCertificateOfCurrency"
                @linkClicked="() => requestCoc(currentPolicy?.policyNumber)"
              ></DocumentLink>
            </div>
          </div>
          <div
            v-if="
              (item.fields?.firstLink.value.class || item.fields?.secondLink.value.class) &&
              certificateDownloadResponse?.length > 0
            "
            class="w-full py-[0.5rem]"
          >
            <InformationBox
              v-if="certificateDownloadResponse === 'coi-error'"
              :description="certificateOfInsuranceDownloadError.content.value"
              :type="InformationBoxTypes.ERROR"
            ></InformationBox>
            <InformationBox
              v-if="certificateDownloadResponse === 'coc-error'"
              :description="certificateOfCurrencyDownloadError.content.value"
              :type="InformationBoxTypes.ERROR"
            ></InformationBox>
          </div>
        </template>
        <template #edit> </template>
      </ReviewSummaryItem>
    </template>
  </SimpleReviewSummary>
</template>
<script setup lang="ts">
import { onMounted, ref } from 'vue';
import { InsurancePolicy, PolicyHolder } from '@/interfaces';
import { InformationBoxTypes } from '@/interfaces/forms/information-box.interface';
import { SelfServiceService } from '@/services/store/self-service-service';
import { replaceBookmark } from '@/utils/bookmark.replace';
import { format as formatDate } from 'date-fns';
import { getFormInput } from '@/utils/form-input-helper';
import SimpleReviewSummary from '@/components/ReviewSummary/SimpleReviewSummary.vue';
import ReviewSummaryItem from '@/components/ReviewSummary/ReviewSummaryItem.vue';
import InformationBox from '@/components/InformationBox/InformationBox.vue';
import DocumentLink from '@/components/DocumentLink/DocumentLink.vue';
import { PolicyStatus } from '@/enums/policy-status.enum';

enum Bookmarks {
  policyStatus = 'policyStatus',
  policyNumber = 'policyNumber',
  policyholders = 'policyholders',
  certificateCurrencyLink = 'certificateCurrencyLink',
  certificateInsuranceLink = 'certificateInsuranceLink',
  policyPeriod = 'policyPeriod',
  insuranceTypeDisplayName = 'insuranceTypeDisplayName',
  vehicleDetails = 'vehicleDetails',
}

const props = defineProps<{
  currentPolicy: InsurancePolicy;
  content: any;
}>();

const policySummary = ref<any>();
const certificateDownloadResponse = ref<any>();
const certificateOfInsuranceDownloadError = ref<any>(
  getFormInput(props.content, 'certificateOfInsuranceDownloadError')
);
const certificateOfCurrencyDownloadError = ref<any>(getFormInput(props.content, 'certificateOfCurrencyDownloadError'));

onMounted(async () => {
  policySummary.value = getPolicySummaryModel();
});

function getPolicySummaryModel(): any {
  const tempPolicySummary = getFormInput(props.content, 'policySummary');
  if (!tempPolicySummary) return { title: '', icon: '', items: [] };
  return {
    title: tempPolicySummary.title,
    icon: tempPolicySummary.fields[0].icon,
    items: tempPolicySummary.fields[0].items,
  };
}

async function downloadDocument(documentStorageId: string) {
  certificateDownloadResponse.value = '';
  try {
    await SelfServiceService.getDownloadDocument(documentStorageId);
  } catch (error) {
    console.error('err', error);
    certificateDownloadResponse.value = 'coi-error';
  }
}

//Sitecore 8 uses request Coc for Certificate of Currency
async function requestCoc(policyNumber: string) {
  certificateDownloadResponse.value = '';

  try {
    await SelfServiceService.requestCoCDocument(policyNumber);
  } catch (error) {
    console.error('coc-err', error);
    certificateDownloadResponse.value = 'coc-error';
  }
}

const getPolicyStatusComponent = (content: any) => {
  if (!props.currentPolicy || !props.currentPolicy?.status) return content;

  const statusIcons = [
    {
      status: PolicyStatus.ACTIVE,
      icon: 'bg-icon-active',
      displayStatus: PolicyStatus.ACTIVE,
    },
    {
      status: PolicyStatus.IN_RENEWAL,
      icon: 'bg-icon-active',
      displayStatus: PolicyStatus.ACTIVE,
    },
    {
      status: PolicyStatus.CANCELLED,
      icon: 'bg-icon-cancelled',
      displayStatus: PolicyStatus.CANCELLED,
    },
    {
      status: PolicyStatus.OVERDUE,
      icon: 'bg-icon-cancelled',
      displayStatus: PolicyStatus.OVERDUE,
    },
  ];

  let useStatusDetails = statusIcons.find((x) => x.status === props.currentPolicy?.status);

  return (
    content.value,
    `<div class="flex">
    <span class="${useStatusDetails?.icon} h-[10px] w-[11px] self-center">&nbsp;</span>
    <span class="pl-2">${useStatusDetails?.displayStatus}</span>
    </div>`
  );
};

const getPolicyholders = () => {
  if (!props.currentPolicy?.policyHolders) return '';

  const policyholders = props.currentPolicy?.policyHolders
    .map((policyholder: PolicyHolder, index) => {
      const isPrimary = index === 0 && props.currentPolicy?.policyHolders.length > 1 ? '(Primary)' : '';
      return `<div class='py-0.5 my-0'>${policyholder.firstName} ${policyholder.lastName} ${isPrimary}</div>`;
    })
    .join('');

  return policyholders;
};

function fillBookmarks(content: Field<string>) {
  if (!props.currentPolicy || !content?.value) return content;

  /* Policy summary */
  content.value = replaceBookmark(Bookmarks.policyStatus, content.value, getPolicyStatusComponent(content));
  content.value = replaceBookmark(Bookmarks.policyNumber, content.value, props.currentPolicy.policyNumber);
  content.value = replaceBookmark(Bookmarks.policyholders, content.value, getPolicyholders());

  if (props.currentPolicy?.termStart && props.currentPolicy?.termEnd) {
    content.value = replaceBookmark(
      Bookmarks.policyPeriod,
      content.value,
      `${formatDate(props.currentPolicy.termStart, 'dd MMMM yyyy')} - ${formatDate(props.currentPolicy.termEnd, 'dd MMMM yyyy')}`
    );
  }

  /* Policy details */
  content.value = replaceBookmark(Bookmarks.vehicleDetails, content.value, props.currentPolicy.policyNumber);

  return content.value;
}
</script>
